import {RequiredType} from 'components/Form/types';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {TooltipWrapperProps} from 'components/Tooltip/types';
import {FC, ReactNode} from 'react';
import {FieldError} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {WithRequiredProperty} from 'types/utils';

import FieldRequiredAsterisk from './FieldRequiredAsterisk';
import FieldRequiredText from './FieldRequiredText';

type FieldLabelProps = {
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    error?: FieldError;
    htmlFor?: string;
    isSideBySide?: boolean;
    required?: boolean;
    requiredType?: RequiredType;
    tooltip?: WithRequiredProperty<TooltipWrapperProps, 'message'>;
};

const FieldLabel: FC<FieldLabelProps> = ({
    children,
    className,
    disabled,
    error,
    htmlFor,
    isSideBySide,
    required,
    requiredType,
    tooltip,
}) => {
    const innerClassName = twMerge(
        htmlFor && 'flex-initial',
        isSideBySide && 'text-right',
        isSideBySide && required && '-mt-1',
    );

    const requiredAsterisk =
        required && requiredType === 'asterisk' ? (
            <FieldRequiredAsterisk />
        ) : null;

    const fontSize = isSideBySide ? 'text-base' : 'text-sm';

    const span = (
        <span
            className={twMerge(
                fontSize,
                !tooltip && innerClassName,
                disabled ? 'text-light-disabled' : 'text-light-secondary',
            )}
        >
            {requiredAsterisk}
            {children}
        </span>
    );

    const label = tooltip ? (
        <div className={twMerge('inline-flex items-start', innerClassName)}>
            {span}
            <TooltipIcon
                className="ml-1 mt-1"
                href={tooltip.href}
                message={tooltip.message}
                size="base"
            />
        </div>
    ) : (
        span
    );

    const requiredLabel =
        required && requiredType === 'text' ? (
            <FieldRequiredText disabled={disabled} error={error} />
        ) : null;

    const outerClassName = twMerge(
        'ml-px',
        isSideBySide ? 'flex flex-col items-end' : 'mb-1 flex items-center',
        className,
    );

    if (htmlFor) {
        return (
            <label className={outerClassName} htmlFor={htmlFor}>
                {label}
                {requiredLabel}
            </label>
        );
    }

    return (
        <div className={outerClassName}>
            {label}
            {requiredLabel}
        </div>
    );
};

export default FieldLabel;
